import { FORM_DATATYPES, TpAutoFormChoice, TpAutoFormFieldDef } from '../../../../components/auto-form-modal/AutoForm';
import { getColumnInfo } from '../../../../data/analyticsDataSlice';
import { VALTYPES } from '../../../../data/interfaces';
import { getDataTable, getDataTableMeta } from '../../charts/chartDataBindings';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpDashboardActionContext } from '../interfaces';

export interface TpDbActionUserPromptType {
  type: string;
  description: string;

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => any;
  getJsonSchema: () => any;
}

//////////////////////// selectSingleItem ////////////////////////////

const selectSingleItem: TpDbActionUserPromptType = {
  type: 'selectSingleItem',
  description: 'Prompts the user for a single item in a data table, storing the identifier of that item in a variable',

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => {
    const dataTable = getDataTable(ctx.dataContext, param.dataTableId);
    const choices: TpAutoFormChoice[] = [];
    const rowIds = dataTable.columnData[dataTable.colItemId];
    const rowNames = dataTable.columnData[dataTable.colLabel] || rowIds;
    for (let i = 0; i < rowIds.length; i++) {
      choices.push({
        id: rowIds[i],
        content: rowNames[i],
      });
    }
    const paramDef: TpAutoFormFieldDef = {
      id: param.id,
      name: param.name,
      dataType: FORM_DATATYPES.MULTIPLECHOICE,
      startContent: null,
      choices,
    };
    return paramDef;
  },

  getJsonSchema: () => {
    return {
      type: 'object',
      title: selectSingleItem.type,
      description: selectSingleItem.description,
      properties: {
        type: { type: 'string', const: selectSingleItem.type },
        id: { type: 'string', description: 'Name of the created variable holding the selected item identifier.' },
        name: { type: 'string', description: 'Displayed name of the parameter in the user prompt modal.' },
        dataTableId: { type: 'string', description: 'Identifier of the data table for which to prompt for an item.' },
      },
      required: ['type', 'id', 'name', 'dataTableId'],
    };
  },
};

//////////////////////// promptString ////////////////////////////

const promptString: TpDbActionUserPromptType = {
  type: 'promptString',
  description: 'Prompts the user for a string',

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => {
    const paramDef: TpAutoFormFieldDef = {
      id: param.id,
      name: param.name,
      dataType: FORM_DATATYPES.STRING,
      startContent: param.defaultValue || '',
      required: !!param.required,
    };
    return paramDef;
  },

  getJsonSchema: () => {
    return {
      type: 'object',
      title: promptString.type,
      description: promptString.description,
      properties: {
        type: { type: 'string', const: promptString.type },
        id: { type: 'string', description: 'Name of the created variable holding the string entered by the user.' },
        name: { type: 'string', description: 'Displayed name of the parameter in the user prompt modal.' },
        defaultValue: { type: 'string', description: 'Default content.' },
        required: { type: 'boolean', description: 'Indicates whether or not the entered string can be empty' },
      },
      required: ['type', 'id', 'name'],
    };
  },
};

//////////////////////// promptNumerical ////////////////////////////

const promptNumerical: TpDbActionUserPromptType = {
  type: 'promptNumerical',
  description: 'Prompts the user for a numerical value',

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => {
    const paramDef: TpAutoFormFieldDef = {
      id: param.id,
      name: param.name,
      dataType: FORM_DATATYPES.NUMBER,
      startContent: param.defaultValue || null,
      required: !!param.required,
    };
    let validationMessage = '';
    if (param.minValue !== undefined) validationMessage = `Should not be smaller than ${param.minValue}`;
    if (param.maxValue !== undefined) validationMessage = `Should not be larger than ${param.minValue}`;
    if (param.minValue !== undefined && param.maxValue !== undefined)
      validationMessage = `Should be between ${param.minValue} and ${param.maxValue}`;
    if (validationMessage) {
      paramDef.ValidationMessage = validationMessage;
      paramDef.inputValidation = (content) => {
        if (param.minValue !== undefined && content < param.minValue) return false;
        if (param.maxValue !== undefined && content > param.maxValue) return false;
        return true;
      };
    }
    return paramDef;
  },

  getJsonSchema: () => {
    return {
      type: 'object',
      title: promptNumerical.type,
      description: promptNumerical.description,
      properties: {
        type: { type: 'string', const: promptNumerical.type },
        id: { type: 'string', description: 'Name of the created variable holding the value entered by the user.' },
        name: { type: 'string', description: 'Displayed name of the parameter in the user prompt modal.' },
        defaultValue: { type: 'number', description: 'Default content.' },
        minValue: { type: 'number', description: 'Minimum value that can be entered.' },
        maxValue: { type: 'number', description: 'Maximum value that can be entered.' },
        required: { type: 'boolean', description: 'Indicates whether or not the entered value can be empty' },
      },
      required: ['type', 'id', 'name'],
    };
  },
};

//////////////////////// promptMultipleChoice ////////////////////////////

const promptMultipleChoice: TpDbActionUserPromptType = {
  type: 'promptMultipleChoice',
  description: 'Prompts the user for a multiple choice',

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => {
    const paramDef: TpAutoFormFieldDef = {
      id: param.id,
      name: param.name,
      dataType: FORM_DATATYPES.MULTIPLECHOICE,
      startContent: param.defaultValue || '',
      required: !!param.required,
      choices: param.choices,
    };
    return paramDef;
  },

  getJsonSchema: () => {
    return {
      type: 'object',
      title: promptMultipleChoice.type,
      description: promptMultipleChoice.description,
      properties: {
        type: { type: 'string', const: promptMultipleChoice.type },
        id: { type: 'string', description: 'Name of the created variable holding the choice entered by the user.' },
        name: { type: 'string', description: 'Displayed name of the parameter in the user prompt modal.' },
        choices: {
          description: 'Set of choices to prompt for',
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', description: 'Identifier of the choice.' },
              content: { type: 'string', description: 'Displayed name of the choice.' },
            },
            required: ['id', 'content'],
          },
        },
        defaultValue: { type: 'string', description: 'Default content.' },
        required: { type: 'boolean', description: 'Indicates whether or not the entered choice can be empty' },
      },
      required: ['type', 'id', 'name', 'choices'],
    };
  },
};

//////////////////////// promptPropertyGroupOptions ////////////////////////////

const promptPropertyGroupOptions: TpDbActionUserPromptType = {
  type: 'promptPropertyGroupOptions',
  description: 'Prompts the user for the different values of PropertyGroup',

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => {
    const dataTable = getDataTableMeta(ctx.dataContext, param.dataTableId);
    const matchingPropertyGroup = dataTable.columnGroups.find((group) => group.id === param.propertyGroup);
    if (!matchingPropertyGroup) throw `Invalid property group: ${param.propertyGroup}`;
    const choices: TpAutoFormChoice[] = dataTable.columns
      .filter((col) => {
        return col.groupId == param.propertyGroup;
      })
      .map((col, index) => {
        return {
          id: col.id,
          content: col.name,
        };
      });
    const paramDef: TpAutoFormFieldDef = {
      id: param.id,
      name: param.name,
      dataType: FORM_DATATYPES.SEARCH,
      startContent: param.defaultValue || '',
      required: !!param.required,
      choices: choices,
    };
    return paramDef;
  },

  getJsonSchema: () => {
    return {
      type: 'object',
      title: promptPropertyGroupOptions.type,
      description: promptPropertyGroupOptions.description,
      properties: {
        type: { type: 'string', const: promptPropertyGroupOptions.type },
        id: { type: 'string', description: 'Name of the created variable holding the choice entered by the user.' },
        name: { type: 'string', description: 'Displayed name of the parameter in the user prompt modal.' },
        propertyGroup: {
          description: 'Property Group to use as widget input',
          type: 'string',
        },
        dataTableId: { type: 'string', description: 'Identifier of the data table for which to prompt for an item.' },
        defaultValue: { type: 'string', description: 'Default content.' },
        required: { type: 'boolean', description: 'Indicates whether or not the entered choice can be empty' },
      },
      required: ['type', 'id', 'name', 'propertyGroup', 'dataTableId'],
    };
  },
};

//////////////////////// promptCategoricalValue ////////////////////////////

const promptCategoricalValue: TpDbActionUserPromptType = {
  type: 'promptCategoricalValue',
  description: 'Prompts the user for a single value from a table column holding categorical values',

  createAutoFormDef: (ctx: TpDashboardActionContext, param: any) => {
    const dataTable = getDataTable(ctx.dataContext, param.dataTableId);
    const choices: TpAutoFormChoice[] = [];
    const columnInfo = getColumnInfo(dataTable, param.colId);
    if (columnInfo.valueType !== VALTYPES.CATEGORICAL && columnInfo.valueType !== VALTYPES.MULTICAT)
      throw `Column should be categorical (${columnInfo.id})`;
    const categories = columnInfo.augmentedData!.categories!.map((cat) =>
      convertColumnDataToDisplayString(columnInfo, cat.value),
    );
    for (const cat of categories)
      choices.push({
        id: cat,
        content: cat,
      });
    const paramDef: TpAutoFormFieldDef = {
      id: param.id,
      name: param.name,
      dataType: param.method == 'search' ? FORM_DATATYPES.SEARCH : FORM_DATATYPES.MULTIPLECHOICE,
      startContent: null,
      choices,
    };
    return paramDef;
  },

  getJsonSchema: () => {
    return {
      type: 'object',
      title: promptCategoricalValue.type,
      description: promptCategoricalValue.description,
      properties: {
        type: { type: 'string', const: promptCategoricalValue.type },
        id: {
          type: 'string',
          description: 'Name of the created variable holding categorical value chosen by the user.',
        },
        name: { type: 'string', description: 'Display name of the parameter in the user prompt modal.' },
        dataTableId: {
          type: 'string',
          description: 'Identifier of the data table for which to prompt for a categorical value.',
        },
        colId: {
          type: 'string',
          description: 'Identifier of the table column for which to prompt for a categorical value.',
        },
        method: {
          type: 'string',
          description: 'Type of selection prompt to be presented in the modal (defaults to "list").',
          enum: ['list', 'search'],
        },
        required: { type: 'boolean', description: 'Indicates whether or not the entered choice can be empty' },
      },
      required: ['type', 'id', 'name', 'dataTableId', 'colId'],
    };
  },
};

///////////////////////////////////////////////////////////////////////

export const dbActionUserPromptTypeList = [
  selectSingleItem,
  promptString,
  promptNumerical,
  promptMultipleChoice,
  promptPropertyGroupOptions,
  promptCategoricalValue,
];

export const dbActionUserPromptTypeMap: { [type: string]: TpDbActionUserPromptType } = {};
for (const type of dbActionUserPromptTypeList) dbActionUserPromptTypeMap[type.type] = type;
